
import { syncPendingSettings, getSettings } from './lib/db/queries';
import { useEffect, useRef, lazy, Suspense } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from 'react-router-dom';
import { useAuthStore } from './store/auth';
import { useSyncStore } from './store/sync';
import { ToastProvider } from './components/ui/Toast';
import { CustomDialogProvider } from './components/ui/CustomDialog';
import FeedbackWidget from './components/FeedbackWidget';

// Auth pages
const WelcomePage = lazy(() => import('./pages/auth/WelcomePage'));
const SignUpPage = lazy(() => import('./pages/auth/SignUpPage'));
const SignInPage = lazy(() => import('./pages/auth/SignInPage'));
const RecoveryCodePage = lazy(() => import('./pages/auth/RecoveryCodePage'));
const PasswordResetPage = lazy(() => import('./pages/auth/PasswordResetPage'));
const PinSetupPage = lazy(() => import('./pages/auth/PinSetupPage'));
const PinEntryPage = lazy(() => import('./pages/auth/PinEntryPage'));
const PinResetPage = lazy(() => import('./pages/auth/PinResetPage'));

// Onboarding
const OnboardingLayout = lazy(() => import('./pages/onboarding/OnboardingLayout'));
const Step1Fund = lazy(() => import('./pages/onboarding/Step1Fund'));
const Step2Bill = lazy(() => import('./pages/onboarding/Step2Bill'));
const Step3Backup = lazy(() => import('./pages/onboarding/Step3Backup'));
const AllSet = lazy(() => import('./pages/onboarding/AllSet'));

// App pages (placeholder until built)
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const FundListPage = lazy(() => import('./pages/FundListPage'));
const FundDetailPage = lazy(() => import('./pages/FundDetailPage'));
const MonthlyChecklistPage = lazy(() => import('./pages/MonthlyChecklistPage'));
const AddEditTransactionPage = lazy(() => import('./pages/AddEditTransactionPage'));
const SyncStatusPage = lazy(() => import('./pages/SyncStatusPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const MigrationPage = lazy(() => import('./pages/MigrationPage'));

// Loading fallback
function PageLoader() {
  return (
    <div className="flex-center" style={{ minHeight: '100dvh' }}>
      <div className="spinner" />
    </div>
  );
}

/** Root redirect: decides where to send user based on auth/pin/onboarding state */
function RootRedirect() {
  const { session, isInitialized, isLoading } = useAuthStore();

  if (!isInitialized || isLoading) return <PageLoader />;
  if (!session) return <Navigate to="/auth/welcome" replace />;
  if (!session.recoveryCodeSaved) return <Navigate to="/auth/recovery-code" replace />;
  if (!session.pinSetup) return <Navigate to="/auth/pin-setup" replace />;
  if (!session.onboardingComplete) return <Navigate to="/onboarding/step1" replace />;
  return <Navigate to="/dashboard" replace />;
}

/** Guard: requires auth. Redirects to appropriate auth step if not met. */
function AuthGuard() {
  const { session, isInitialized, isLoading, masterKey } = useAuthStore();
  const { restoreFromDrive, driveConnected } = useSyncStore();

  // Fire restore exactly once per session after the user unlocks
  const hasRestoredRef = useRef(false);

  useEffect(() => {
    if (masterKey && session && driveConnected && !hasRestoredRef.current) {
      hasRestoredRef.current = true;
      restoreFromDrive(session.uid, masterKey).catch(err => {
        console.warn('Initial restore from drive failed (possibly key mismatch):', err);
      });
    }
  }, [masterKey, session, driveConnected, restoreFromDrive]);
  if (!isInitialized || isLoading) return <PageLoader />;
  if (!session) return <Navigate to="/auth/welcome" replace />;
  if (!session.recoveryCodeSaved) return <Navigate to="/auth/recovery-code" replace />;
  if (!session.pinSetup) return <Navigate to="/auth/pin-setup" replace />;
  if (!session.onboardingComplete) return <Navigate to="/onboarding/step1" replace />;
  // PIN is set and onboarding done — need masterKey to proceed (unlocked via PIN/biometric)
  if (!masterKey) return <Navigate to="/auth/pin-entry" replace />;
  return <Outlet />;
}

/** Guard: only accessible when NOT logged in (or not fully set up) */
function GuestGuard() {
  const { session, isInitialized, isLoading, masterKey } = useAuthStore();
  const location = useLocation();

  if (!isInitialized || isLoading) return <PageLoader />;

  if (session && session.recoveryCodeSaved && session.pinSetup && session.onboardingComplete && masterKey) {
    return <Navigate to="/dashboard" replace />;
  }

  // If partially authenticated, enforce the flow and prevent going backwards or staying on welcome
  if (session) {
    if (!session.recoveryCodeSaved && location.pathname !== '/auth/recovery-code') {
      return <Navigate to="/auth/recovery-code" replace />;
    }
    if (session.recoveryCodeSaved && !session.pinSetup && location.pathname !== '/auth/pin-setup') {
      return <Navigate to="/auth/pin-setup" replace />;
    }
    if (session.recoveryCodeSaved && session.pinSetup && !session.onboardingComplete && !location.pathname.startsWith('/onboarding')) {
      return <Navigate to="/onboarding/step1" replace />;
    }
    if (session.recoveryCodeSaved && session.pinSetup && session.onboardingComplete && !masterKey && location.pathname !== '/auth/pin-entry') {
      return <Navigate to="/auth/pin-entry" replace />;
    }
  }

  return <Outlet />;
}

function AppRoutes() {
  const { initialize, session } = useAuthStore();

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    if (session?.uid) {
      syncPendingSettings(session.uid);
    }

    const handleOnline = () => {
      if (session?.uid) {
        syncPendingSettings(session.uid);
      }
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [session?.uid]);

  const { driveConnected, isSyncing, triggerSync } = useSyncStore();
  const { masterKey } = useAuthStore();

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    const handleDataChanged = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        if (session?.uid && driveConnected && !isSyncing) {
          getSettings(session.uid).then(s => {
            if (s.autoSync) {
              triggerSync(session.uid, masterKey).catch(() => { });
            }
          });
        }
      }, 3000); // 3s debounce
    };

    window.addEventListener('sorted-data-changed', handleDataChanged);
    return () => {
      window.removeEventListener('sorted-data-changed', handleDataChanged);
      clearTimeout(timeout);
    };
  }, [session, driveConnected, isSyncing, triggerSync, masterKey]);

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Root redirect */}
        <Route path="/" element={<RootRedirect />} />

        {/* Auth routes (guest only) */}
        <Route element={<GuestGuard />}>
          <Route path="/auth/welcome" element={<WelcomePage />} />
          <Route path="/auth/signup" element={<SignUpPage />} />
          <Route path="/auth/signin" element={<SignInPage />} />
          <Route path="/auth/recovery-code" element={<RecoveryCodePage />} />
          <Route path="/auth/password-reset" element={<PasswordResetPage />} />
          <Route path="/auth/pin-setup" element={<PinSetupPage />} />
          <Route path="/auth/pin-entry" element={<PinEntryPage />} />
          <Route path="/auth/pin-reset" element={<PinResetPage />} />
        </Route>

        {/* Onboarding routes */}
        <Route path="/onboarding" element={<OnboardingLayout />}>
          <Route index element={<Navigate to="step1" replace />} />
          <Route path="step1" element={<Step1Fund />} />
          <Route path="step2" element={<Step2Bill />} />
          <Route path="step3" element={<Step3Backup />} />
          <Route path="allset" element={<AllSet />} />
        </Route>

        {/* Protected app routes */}
        <Route element={<AuthGuard />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/funds" element={<FundListPage />} />
          <Route path="/checklist" element={<MonthlyChecklistPage />} />
          <Route path="/funds/:fundId" element={<FundDetailPage />} />
          <Route path="/funds/:fundId/bills/new" element={<AddEditTransactionPage />} />
          <Route path="/funds/:fundId/bills/:billId" element={<AddEditTransactionPage />} />
          <Route path="/funds/:fundId/expenses/new" element={<AddEditTransactionPage />} />
          <Route path="/funds/:fundId/expenses/:expenseId" element={<AddEditTransactionPage />} />
          <Route path="/funds/:fundId/recurring/new" element={<AddEditTransactionPage />} />
          <Route path="/funds/:fundId/recurring/:recurringId" element={<AddEditTransactionPage />} />
          <Route path="/sync" element={<SyncStatusPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/migration" element={<MigrationPage />} />
        </Route>

        {/* 404 fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <CustomDialogProvider>
          <FeedbackWidget />
          <AppRoutes />
        </CustomDialogProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}
